import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { fmtDate } from '../../lib/constants';
import StatCard from '../../components/ui/StatCard';
import toast from 'react-hot-toast';

const DISTRICTS = ['Upendo', 'Wema', 'Furaha', 'Fadhili', 'Amani', 'Uaminifu'];

export default function AttendancePage() {
  const [tab, setTab] = useState('communion');
  const [services, setServices] = useState([]);
  const [selectedSvc, setSelectedSvc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());

  // Add service form – extended with new fields
  const blankSvc = {
    date: new Date().toISOString().split('T')[0],
    service_type: 'Sunday Service',
    sermon_title: '',
    preacher: '',
    total_attendance: '',
    // New fields
    readings: '',
    sermon_summary: '',
    tithe_offering: 0,
    main_offering: 0,
    thanksgiving_offering: 0,
    firstfruits_offering: 0,
    notes: '',
  };
  const [svcForm, setSvcForm] = useState({ ...blankSvc });
  const [savingSvc, setSavingSvc] = useState(false);

  // Holy Communion state (unchanged)
  const [commMembers, setCommMembers] = useState([]);
  const [commMarked, setCommMarked] = useState({});
  const [commDistrict, setCommDistrict] = useState('');
  const [commSearch, setCommSearch] = useState('');
  const [commSaving, setCommSaving] = useState({});
  const [commLoading, setCommLoading] = useState(false);

  useEffect(() => {
    loadServices();
  }, [year]);

  async function loadServices() {
    setLoading(true);
    const { data } = await supabase
      .from('sunday_services')
      .select('*')
      .gte('date', `${year}-01-01`)
      .lte('date', `${year}-12-31`)
      .order('date', { ascending: false });
    setServices(data || []);
    if (data?.length && !selectedSvc) setSelectedSvc(data[0]);
    setLoading(false);
  }

  useEffect(() => {
    if (selectedSvc) loadCommunionData(selectedSvc.id);
  }, [selectedSvc]);

  async function loadCommunionData(serviceId) {
    setCommLoading(true);
    setCommDistrict('');
    setCommSearch('');
    setCommMarked({});

    const { data: members } = await supabase
      .from('members')
      .select('id, first_name, last_name, member_number, districts(name), membership_type')
      .eq('status', 'active')
      .in('membership_type', ['Full Member', 'Communicant'])
      .order('districts(name)')
      .order('first_name');

    const { data: marked } = await supabase
      .from('attendance')
      .select('id, member_id, status')
      .eq('service_id', serviceId)
      .eq('type', 'holy_communion')
      .eq('status', 'present');

    const markedMap = {};
    (marked || []).forEach((rec) => {
      markedMap[rec.member_id] = { id: rec.id, status: rec.status };
    });

    setCommMembers(members || []);
    setCommMarked(markedMap);
    setCommLoading(false);
  }

  async function togglePresent(member) {
    setCommSaving((s) => ({ ...s, [member.id]: true }));
    const existing = commMarked[member.id];

    if (existing) {
      await supabase.from('attendance').delete().eq('id', existing.id);
      setCommMarked((prev) => {
        const copy = { ...prev };
        delete copy[member.id];
        return copy;
      });
      toast.success(`${member.first_name} unmarked`);
    } else {
      const { data, error } = await supabase
        .from('attendance')
        .insert({
          service_id: selectedSvc.id,
          member_id: member.id,
          type: 'holy_communion',
          status: 'present',
        })
        .select()
        .single();
      if (error) {
        toast.error(error.message);
        setCommSaving((s) => ({ ...s, [member.id]: false }));
        return;
      }
      setCommMarked((prev) => ({ ...prev, [member.id]: { id: data.id, status: 'present' } }));
      toast.success(`${member.first_name} marked present ✅`);
    }

    const newPresentCount = Object.values(commMarked).filter((v) => v?.status === 'present').length;
    await supabase
      .from('sunday_services')
      .update({ communion_count: newPresentCount })
      .eq('id', selectedSvc.id);
    await loadServices();
    setCommSaving((s) => ({ ...s, [member.id]: false }));
  }

  async function markAllPresent() {
    const filtered = getFilteredMembers();
    if (!window.confirm(`Mark all ${filtered.length} visible members as Present?`)) return;
    setCommLoading(true);
    let addedCount = 0;
    for (const m of filtered) {
      const existing = commMarked[m.id];
      if (!existing) {
        const { data } = await supabase
          .from('attendance')
          .insert({
            service_id: selectedSvc.id,
            member_id: m.id,
            type: 'holy_communion',
            status: 'present',
          })
          .select()
          .single();
        if (data) {
          setCommMarked((prev) => ({ ...prev, [m.id]: { id: data.id, status: 'present' } }));
          addedCount++;
        }
      }
    }
    const newPresentCount = Object.keys(commMarked).length + addedCount;
    await supabase
      .from('sunday_services')
      .update({ communion_count: newPresentCount })
      .eq('id', selectedSvc.id);
    await loadServices();
    toast.success(`Marked ${addedCount} member(s) present ✅`);
    setCommLoading(false);
  }

  function getFilteredMembers() {
    return commMembers.filter((m) => {
      const matchDist = !commDistrict || m.districts?.name === commDistrict;
      const matchSearch =
        !commSearch ||
        `${m.first_name} ${m.last_name} ${m.member_number}`
          .toLowerCase()
          .includes(commSearch.toLowerCase());
      return matchDist && matchSearch;
    });
  }

  // Compute total offering from breakdown for the form
  const computeTotalOffering = (form) => {
    return (form.tithe_offering || 0) +
           (form.main_offering || 0) +
           (form.thanksgiving_offering || 0) +
           (form.firstfruits_offering || 0);
  };

  async function handleAddService(e) {
    e.preventDefault();
    setSavingSvc(true);
    const total = computeTotalOffering(svcForm);
    const payload = {
      ...svcForm,
      total_attendance: svcForm.total_attendance ? Number(svcForm.total_attendance) : null,
      total_offering: total, // auto-calculated from breakdown
      tithe_offering: Number(svcForm.tithe_offering) || 0,
      main_offering: Number(svcForm.main_offering) || 0,
      thanksgiving_offering: Number(svcForm.thanksgiving_offering) || 0,
      firstfruits_offering: Number(svcForm.firstfruits_offering) || 0,
    };
    const { error } = await supabase.from('sunday_services').insert(payload);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Service saved ✅');
      setSvcForm({ ...blankSvc });
      await loadServices();
      setTab('communion');
    }
    setSavingSvc(false);
  }

  async function deleteService(serviceId) {
    if (!window.confirm('Delete this service and all its communion records?')) return;
    await supabase.from('attendance').delete().eq('service_id', serviceId);
    await supabase.from('sunday_services').delete().eq('id', serviceId);
    toast.success('Deleted');
    await loadServices();
    if (services.length > 1) setSelectedSvc(services[0]);
    else setSelectedSvc(null);
  }

  const presentCount = Object.keys(commMarked).length;
  const totalFullMembers = commMembers.length;
  const attendanceRate = totalFullMembers > 0 ? Math.round((presentCount / totalFullMembers) * 100) : 0;
  const filteredMembers = getFilteredMembers();

  // For selected service, show breakdown
  const selectedBreakdown = selectedSvc ? {
    tithe: selectedSvc.tithe_offering || 0,
    main: selectedSvc.main_offering || 0,
    thanksgiving: selectedSvc.thanksgiving_offering || 0,
    firstfruits: selectedSvc.firstfruits_offering || 0,
    total: (selectedSvc.tithe_offering || 0) +
           (selectedSvc.main_offering || 0) +
           (selectedSvc.thanksgiving_offering || 0) +
           (selectedSvc.firstfruits_offering || 0),
  } : null;

  return (
    <div>
      {/* Header */}
      <div className="flex-between mb-4">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800 }}>📋 Attendance & Services</h1>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>
            Track Holy Communion & manage services
          </div>
        </div>
        <div className="flex gap-2">
          <select
            className="form-select"
            style={{ width: 'auto' }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {[2026, 2025, 2024, 2023].map((y) => (
              <option key={y}>{y}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={() => setTab('add')}>
            + Add Service
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${tab === 'communion' ? 'active' : ''}`}
          onClick={() => setTab('communion')}
        >
          🍞 Holy Communion
        </button>
        <button className={`tab ${tab === 'add' ? 'active' : ''}`} onClick={() => setTab('add')}>
          ➕ Add Service
        </button>
      </div>

      {/* ========== HOLY COMMUNION TAB ========== */}
      {tab === 'communion' && (
        <div>
          <div className="card mb-4">
            <div className="card-pad">
              <div className="grid-2" style={{ alignItems: 'end' }}>
                <div className="form-group">
                  <label className="form-label">Select Service</label>
                  <select
                    className="form-select"
                    value={selectedSvc?.id || ''}
                    onChange={(e) => {
                      const svc = services.find((s) => s.id === e.target.value);
                      setSelectedSvc(svc);
                    }}
                    disabled={loading || services.length === 0}
                  >
                    {services.map((svc) => (
                      <option key={svc.id} value={svc.id}>
                        {fmtDate(svc.date)} — {svc.sermon_title || svc.service_type}
                      </option>
                    ))}
                  </select>
                  {services.length === 0 && !loading && (
                    <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>
                      No services for {year}. Click "+ Add Service"
                    </div>
                  )}
                </div>
                {selectedSvc && (
                  <div className="flex gap-2" style={{ justifyContent: 'flex-end' }}>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => deleteService(selectedSvc.id)}
                    >
                      🗑 Delete Service
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {selectedSvc && (
            <>
              {/* Stats cards – extended with breakdown */}
              <div className="grid-4 mb-4">
                <StatCard icon="📅" label="Service Date" value={fmtDate(selectedSvc.date)} color="red" />
                <StatCard
                  icon="👥"
                  label="Total Attendance"
                  value={selectedSvc.total_attendance || '—'}
                  color="green"
                  sub="recorded headcount"
                />
                <StatCard
                  icon="🍞"
                  label="Communion Present"
                  value={`${presentCount} / ${totalFullMembers}`}
                  color="amber"
                  sub={`${attendanceRate}% of full members`}
                />
                <StatCard
                  icon="💰"
                  label="Total Offering (KES)"
                  value={selectedSvc.total_offering ? selectedSvc.total_offering.toLocaleString() : '—'}
                  color="blue"
                />
              </div>

              {/* Offering breakdown (new) */}
              {selectedBreakdown && (selectedBreakdown.tithe > 0 || selectedBreakdown.main > 0 || selectedBreakdown.thanksgiving > 0 || selectedBreakdown.firstfruits > 0) && (
                <div className="card mb-3">
                  <div className="card-pad" style={{ paddingTop: 10, paddingBottom: 10, display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
                    <div><span style={{ color: 'var(--text3)' }}>Tithe:</span> <strong>KES {selectedBreakdown.tithe.toLocaleString()}</strong></div>
                    <div><span style={{ color: 'var(--text3)' }}>Main Offering:</span> <strong>KES {selectedBreakdown.main.toLocaleString()}</strong></div>
                    <div><span style={{ color: 'var(--text3)' }}>Thanksgiving:</span> <strong>KES {selectedBreakdown.thanksgiving.toLocaleString()}</strong></div>
                    <div><span style={{ color: 'var(--text3)' }}>Firstfruits:</span> <strong>KES {selectedBreakdown.firstfruits.toLocaleString()}</strong></div>
                    <div><span style={{ color: 'var(--text3)' }}>Total:</span> <strong style={{ color: 'var(--blue-dark)' }}>KES {selectedBreakdown.total.toLocaleString()}</strong></div>
                  </div>
                </div>
              )}

              {/* Filters etc. */}
              <div className="card mb-3">
                <div className="card-pad" style={{ paddingTop: 10, paddingBottom: 10 }}>
                  <div className="flex gap-2 flex-wrap">
                    <div className="search-bar" style={{ flex: '1 1 200px' }}>
                      🔍
                      <input
                        value={commSearch}
                        onChange={(e) => setCommSearch(e.target.value)}
                        placeholder="Search name or member #"
                      />
                      {commSearch && (
                        <button
                          onClick={() => setCommSearch('')}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--text3)',
                            cursor: 'pointer',
                            fontSize: 16,
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <select
                      className="form-select"
                      style={{ width: 'auto' }}
                      value={commDistrict}
                      onChange={(e) => setCommDistrict(e.target.value)}
                    >
                      <option value="">All Districts ({commMembers.length})</option>
                      {DISTRICTS.map((d) => {
                        const cnt = commMembers.filter((m) => m.districts?.name === d).length;
                        return cnt > 0 ? (
                          <option key={d} value={d}>
                            {d} ({cnt})
                          </option>
                        ) : null;
                      })}
                    </select>
                    <button
                      className="btn btn-sm"
                      style={{
                        background: 'var(--success-bg)',
                        color: 'var(--success)',
                        border: '1px solid var(--success)',
                        fontWeight: 700,
                      }}
                      onClick={markAllPresent}
                      disabled={commLoading}
                    >
                      ✅ Mark All Present
                    </button>
                    <div style={{ fontSize: 12, color: 'var(--text3)', alignSelf: 'center' }}>
                      {filteredMembers.length} member{filteredMembers.length !== 1 && 's'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Member list – unchanged */}
              {commLoading ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
                  Loading members…
                </div>
              ) : commMembers.length === 0 ? (
                <div className="alert alert-warn">
                  No Full Members or Communicants found. Please add members with the correct membership type.
                </div>
              ) : (
                <div className="card">
                  {filteredMembers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 32, color: 'var(--text3)' }}>
                      No members match the filter.
                    </div>
                  ) : (
                    filteredMembers.map((member, idx) => {
                      const isPresent = !!commMarked[member.id];
                      const saving = commSaving[member.id];

                      return (
                        <div
                          key={member.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 14,
                            padding: '12px 16px',
                            borderBottom:
                              idx < filteredMembers.length - 1 ? '1px solid var(--border)' : 'none',
                            background: isPresent ? '#F1F8E9' : 'white',
                            transition: 'background 0.15s',
                          }}
                        >
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: '50%',
                              flexShrink: 0,
                              background: isPresent
                                ? 'rgba(46,125,50,0.12)'
                                : 'var(--blue-light)',
                              color: isPresent ? '#1B5E20' : 'var(--blue-dark)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 13,
                              fontWeight: 700,
                            }}
                          >
                            {member.first_name[0]}
                            {member.last_name[0]}
                          </div>

                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>
                              {member.first_name} {member.last_name}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                              {member.member_number} · {member.membership_type} ·{' '}
                              {member.districts?.name || '—'}
                            </div>
                          </div>

                          <button
                            disabled={saving}
                            onClick={() => togglePresent(member)}
                            className="btn btn-sm"
                            style={{
                              background: isPresent ? '#2E7D32' : 'white',
                              color: isPresent ? 'white' : '#2E7D32',
                              border: '2px solid #2E7D32',
                              fontWeight: 700,
                              padding: '4px 16px',
                              minWidth: 100,
                            }}
                          >
                            {isPresent ? '✅ Present' : '◻️ Mark Present'}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              <div
                className="mt-4"
                style={{
                  padding: '12px 16px',
                  background: 'var(--bg)',
                  borderRadius: 10,
                  display: 'flex',
                  gap: 24,
                  flexWrap: 'wrap',
                  fontSize: 13,
                }}
              >
                <div>
                  <span style={{ color: 'var(--text3)' }}>Total Full Members: </span>
                  <strong>{totalFullMembers}</strong>
                </div>
                <div>
                  <span style={{ color: 'var(--text3)' }}>Present: </span>
                  <strong style={{ color: 'var(--success)' }}>✅ {presentCount}</strong>
                </div>
                <div>
                  <span style={{ color: 'var(--text3)' }}>Not marked: </span>
                  <strong>— {totalFullMembers - presentCount}</strong>
                </div>
                <div>
                  <span style={{ color: 'var(--text3)' }}>Participation rate: </span>
                  <strong style={{ color: 'var(--blue-dark)' }}>{attendanceRate}%</strong>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ========== ADD SERVICE TAB ========== */}
      {tab === 'add' && (
        <div className="card" style={{ maxWidth: 800 }}>
          <div className="card-header">
            <h3>Add New Service</h3>
          </div>
          <div className="card-pad">
            <form onSubmit={handleAddService}>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input
                    className="form-input"
                    type="date"
                    value={svcForm.date}
                    onChange={(e) => setSvcForm((s) => ({ ...s, date: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Service Type</label>
                  <select
                    className="form-select"
                    value={svcForm.service_type}
                    onChange={(e) => setSvcForm((s) => ({ ...s, service_type: e.target.value }))}
                  >
                    {[
                      'Sunday Service',
                      'Midweek Service',
                      'Special Service',
                      'Revival',
                      'Funeral Service',
                      'Wedding Service',
                      'Other',
                    ].map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Sermon Title</label>
                  <input
                    className="form-input"
                    value={svcForm.sermon_title}
                    onChange={(e) => setSvcForm((s) => ({ ...s, sermon_title: e.target.value }))}
                    placeholder="e.g. Walking in Faith"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Preacher / Officiant</label>
                  <input
                    className="form-input"
                    value={svcForm.preacher}
                    onChange={(e) => setSvcForm((s) => ({ ...s, preacher: e.target.value }))}
                    placeholder="Rev. Kamau"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Total Attendance (headcount)</label>
                  <input
                    className="form-input"
                    type="number"
                    min="0"
                    value={svcForm.total_attendance}
                    onChange={(e) => setSvcForm((s) => ({ ...s, total_attendance: e.target.value }))}
                    placeholder="e.g. 120"
                  />
                </div>

                {/* NEW: Readings */}
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Readings (Scripture References)</label>
                  <textarea
                    className="form-textarea"
                    rows={2}
                    value={svcForm.readings}
                    onChange={(e) => setSvcForm((s) => ({ ...s, readings: e.target.value }))}
                    placeholder="e.g. Psalm 23, John 10:1-10"
                  />
                </div>

                {/* NEW: Sermon Summary */}
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Sermon Summary</label>
                  <textarea
                    className="form-textarea"
                    rows={3}
                    value={svcForm.sermon_summary}
                    onChange={(e) => setSvcForm((s) => ({ ...s, sermon_summary: e.target.value }))}
                    placeholder="Brief summary of the sermon..."
                  />
                </div>

                {/* NEW: Offering Breakdown */}
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label style={{ fontWeight: 700, marginBottom: 6, display: 'block' }}>Offering Breakdown (KES)</label>
                  <div className="grid-2">
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: 12 }}>Tithe</label>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        step="1"
                        value={svcForm.tithe_offering}
                        onChange={(e) => setSvcForm((s) => ({ ...s, tithe_offering: parseFloat(e.target.value) || 0 }))}
                        placeholder="0"
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: 12 }}>Main Offering</label>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        step="1"
                        value={svcForm.main_offering}
                        onChange={(e) => setSvcForm((s) => ({ ...s, main_offering: parseFloat(e.target.value) || 0 }))}
                        placeholder="0"
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: 12 }}>Thanksgiving</label>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        step="1"
                        value={svcForm.thanksgiving_offering}
                        onChange={(e) => setSvcForm((s) => ({ ...s, thanksgiving_offering: parseFloat(e.target.value) || 0 }))}
                        placeholder="0"
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: 12 }}>Firstfruits</label>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        step="1"
                        value={svcForm.firstfruits_offering}
                        onChange={(e) => setSvcForm((s) => ({ ...s, firstfruits_offering: parseFloat(e.target.value) || 0 }))}
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 13, background: 'var(--bg)', padding: '6px 12px', borderRadius: 6 }}>
                    <strong>Total Offering (auto-calculated):</strong> KES{' '}
                    {computeTotalOffering(svcForm).toLocaleString()}
                  </div>
                </div>

                {/* Notes */}
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-textarea"
                    rows={2}
                    value={svcForm.notes}
                    onChange={(e) => setSvcForm((s) => ({ ...s, notes: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-3">
                <button type="submit" className="btn btn-primary" disabled={savingSvc}>
                  {savingSvc ? 'Saving…' : '+ Save Service'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setSvcForm({ ...blankSvc });
                    setTab('communion');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}